import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Chip, 
  TextField, 
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CircleIcon from '@mui/icons-material/Circle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import { IAccount } from '../service/accountService';

interface AccountListProps {
  accounts: IAccount[];
  onSelectAccount: (account: IAccount) => void;
}

const AccountList: React.FC<AccountListProps> = ({ accounts, onSelectAccount }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [adminFilter, setAdminFilter] = useState<string>('all');

  // Filtern der Accounts basierend auf den Suchkriterien
  const filteredAccounts = accounts.filter(account => {
    const matchesSearch = 
      account.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesAdminFilter = 
      adminFilter === 'all' || 
      (adminFilter === 'admin' && account.isAdmin) || 
      (adminFilter === 'user' && !account.isAdmin);
    
    return matchesSearch && matchesAdminFilter;
  });

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleAdminFilterChange = (event: SelectChangeEvent<string>) => {
    setAdminFilter(event.target.value);
  };

  // Format das Datum für bessere Lesbarkeit
  const formatDate = (dateString?: Date): string => {
    if (!dateString) return 'Kein Datum';
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <TextField
          placeholder="Account suchen..."
          value={searchTerm}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          sx={{ flexGrow: 1, minWidth: '200px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="admin-filter-label">Benutzertyp</InputLabel>
          <Select
            labelId="admin-filter-label"
            value={adminFilter}
            label="Benutzertyp"
            onChange={handleAdminFilterChange}
            startAdornment={
              <InputAdornment position="start">
                <FilterListIcon />
              </InputAdornment>
            }
          >
            <MenuItem value="all">Alle</MenuItem>
            <MenuItem value="admin">Admins</MenuItem>
            <MenuItem value="user">Benutzer</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={2}>
        {filteredAccounts.length === 0 ? (
          <Grid item xs={12}>
            <Typography variant="body1" sx={{ textAlign: 'center', py: 4 }}>
              Keine Accounts gefunden.
            </Typography>
          </Grid>
        ) : (
          filteredAccounts.map(account => (
            <Grid item xs={12} sm={6} md={4} key={account._id}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.1)',
                  }
                }}
                onClick={() => onSelectAccount(account)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                      {account.displayName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {(account.raspberryPi || account.assignedRaspberryPiRef) && (
                        <Tooltip title="Raspberry Pi zugewiesen">
                          <DeveloperBoardIcon color="primary" fontSize="small" />
                        </Tooltip>
                      )}
                      {account.isAdmin ? (
                        <Chip 
                          icon={<AdminPanelSettingsIcon sx={{ fontSize: '1rem !important' }} />}
                          label="Admin"
                          size="small"
                          color="primary"
                          sx={{ fontWeight: 'bold' }}
                        />
                      ) : (
                        <Chip 
                          icon={<PersonIcon sx={{ fontSize: '1rem !important' }} />}
                          label="Benutzer"
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 'bold' }}
                        />
                      )}
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>E-Mail:</strong> {account.email}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>Kameras:</strong> {account.cameras?.length || 0}
                  </Typography>
                  
                  {(account.raspberryPi || account.assignedRaspberryPiRef) && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>Raspberry Pi:</strong> {account.raspberryPi?.name || account.assignedRaspberryPiRef}
                    </Typography>
                  )}
                  
                  {account.subscribedUntil && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Abo bis:</strong> {formatDate(account.subscribedUntil)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>
    </Box>
  );
};

export default AccountList; 